import { useQuery, useQueryClient } from "@tanstack/react-query";

import { HttpFile } from "@nens/rana/api-v1";
import { getRanaApiV1 } from "@nens/rana/api-v1";

import { combineQueries } from "./utils/combine-queries";
import { ensureObjectUrlCleanup } from "./utils/ensure-object-url-cleanup";

// Single

const getPublicationThumbnailQuery = (publicationId: string | undefined) => {
  return getRanaApiV1().getQueryOptions<HttpFile | null | undefined>({
    queryKey: ["publication", "thumbnail", publicationId],
    queryFn: async () => {
      if (!publicationId) throw Error("never happens");

      return getRanaApiV1().PublicationsApi.publicationThumbnailRetrieve({
        publicationId,
      });
    },
    enabled: !!publicationId,
  });
};

export const usePublicationThumbnailQuery = (publicationId: string | undefined) => {
  return useQuery(getPublicationThumbnailQuery(publicationId));
};

// URL

const useEnsurePublicationThumbnailUrlQueryCleanupSubscription = () => {
  const queryClient = useQueryClient();
  ensureObjectUrlCleanup({
    queryClient,
    queryKeyPrefix: getPublicationThumbnailUrlQuery("", null).queryKey.slice(0, -1),
    subscriptionKey: "publication-thumbnail-url-cleanup",
  });
};

const getPublicationThumbnailUrlQuery = (
  publicationId: string,
  thumbnail: HttpFile | null | undefined
) => {
  return getRanaApiV1().getQueryOptions<string>({
    queryKey: ["publication", "thumbnail-url", publicationId],
    queryFn: async () => {
      if (!thumbnail) throw Error("never happens");
      return URL.createObjectURL(thumbnail);
    },
    enabled: !!thumbnail,
  });
};

export const usePublicationThumbnailUrlQuery = (publicationId: string) => {
  useEnsurePublicationThumbnailUrlQueryCleanupSubscription();
  const thumbnailQuery = usePublicationThumbnailQuery(publicationId);
  const thumbnailUrlQuery = useQuery(
    getPublicationThumbnailUrlQuery(publicationId, thumbnailQuery.data)
  );
  return combineQueries(thumbnailUrlQuery, thumbnailQuery);
};

// INVALIDATION

export const useInvalidatePublicationThumbnailQuery = () => {
  const queryClient = useQueryClient();
  useEnsurePublicationThumbnailUrlQueryCleanupSubscription();
  return async (publicationId: string) => {
    // Using Promise.all could cause a race condition where the thumbnail URL query is invalidated before the thumbnail query, resulting in it using the old thumbnail to create a new object URL. Therefore, we invalidate the queries sequentially.
    await queryClient.invalidateQueries({
      queryKey: getPublicationThumbnailQuery(publicationId).queryKey,
      refetchType: "all",
    });
    await queryClient.invalidateQueries({
      queryKey: getPublicationThumbnailUrlQuery(publicationId, null).queryKey,
      refetchType: "all",
    });
  };
};

export const useUpdatePublicationThumbnailQuery = () => {
  const queryClient = useQueryClient();
  useEnsurePublicationThumbnailUrlQueryCleanupSubscription();
  return async (publicationId: string, newThumbnail: HttpFile | null) => {
    queryClient.setQueryData<HttpFile | null>(
      getPublicationThumbnailQuery(publicationId).queryKey,
      newThumbnail
    );
    queryClient.setQueryData<string | null>(
      getPublicationThumbnailUrlQuery(publicationId, newThumbnail).queryKey,
      newThumbnail ? URL.createObjectURL(newThumbnail) : null
    );
  };
};
